import {useMutation, useQuery, useQueryClient} from "react-query";
import {apiCall} from "./BaseApi";
import {IInvoiceDto} from "./Models/IInvoiceDto";
import {saveAs} from "file-saver";
import { OrderInvoiceDto } from "./Models/OrderInvoiceDto";
import {ORDERS_CACHE_KEY} from "./OrderApi";
import {
    CustomerOrderNotificationType,
    ICustomerOrderNotification, OrderNotificationDto,
    OrderStatus
} from "./Models/IOrderDto";
export const ORDER_INVOICES_CACHE_KEY = 'ORDER_INVOICES_CACHE_KEY '
export const useInvoicesForOrder = (orderId: number) => {
    return useQuery<IInvoiceDto[]>([ORDER_INVOICES_CACHE_KEY, orderId], async () => {
        return await apiCall<IInvoiceDto[]>({
            url: `/invoices/order/${orderId}`,
            method: 'GET',
        })
    });
}

export const useCheckIfOrdersHasInvoice = () => {
    return useMutation(async (ids: number[]) => {
        return await apiCall<OrderInvoiceDto[]>({
            url: `/invoices/orders/validate`,
            method: 'POST',
            data: ids
        })
    })
}

export const useChangeOrderStatusForMultipleOrders = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: {orderIds: number[], orderStatus: OrderStatus}) => {
        return await apiCall({
            url: `/orders/status`,
            method: 'POST',
            data: model
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useChangeTagsForMultipleOrders = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: {orderIds: number[], addTags: number[], removeTags: number[]}) => {
        return await apiCall({
            url: `/orders/tags`,
            method: 'POST',
            data: model
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useInvoiceToSettlementOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: {orderId: number, settlementOrderId: number}) => {
        return await apiCall({
            url: `/orders/${model.orderId}/invoiceToSettlementOrder`,
            method: 'POST',
            data: model
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useSendCustomerOrderNotification = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: OrderNotificationDto) => {
        return await apiCall({
            url: `/orders/${model.orderId}/customerNotification`,
            method: 'POST',
            data: model
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useCustomerOrderNotificationPreview = ( orderId: number, type: CustomerOrderNotificationType, enabled: boolean ) => {
    return useQuery<ICustomerOrderNotification>(
        ['CUSTOMER_ORDER_NOTIFICATION_PREVIEW', orderId, type],
        async () => {
            return await apiCall<ICustomerOrderNotification>({
                url: `/orders/${orderId}/customerNotification`,
                method: 'GET',
                queryParams: { type }
            });
        },
        { enabled }
    );
};

export const useInvoiceToSettlementEntry = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: {orderId: number, feeReceiver: 'AnotherSolution' | 'Beatdown'}) => {
        return await apiCall({
            url: `/orders/${model.orderId}/invoiceToSettlement?receiver=${model.feeReceiver}`,
            method: 'POST',
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useCreateBeatdownPurchaseOrder = () => {
    const queryClient = useQueryClient();

    return useMutation(async (model: {orderIds: number[]}) => {
        return await apiCall({
            url: `/orders/beatdown/purchaseOrder`,
            method: 'POST',
            data: model.orderIds
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}

export const useCreateInvoiceDraftForMultipleOrders = () => {
    const queryClient = useQueryClient();

    return useMutation(async (ids: number[]) => {
        return await apiCall<IInvoiceDto>({
            url: `/invoices/orders`,
            method: 'POST',
            data: ids
        })
    }, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(ORDER_INVOICES_CACHE_KEY)
            await queryClient.invalidateQueries(ORDERS_CACHE_KEY)
        }
    })
}


export const downloadInvoice = async (invoice: IInvoiceDto) => {
    const responseBlob = await apiCall<Blob>({
        responseType: 'blob',
        url: `/invoices/${invoice.id}/pdf`,
        method: 'GET',
    });

    const blob = new Blob([responseBlob], {type: 'application/pdf' });

    saveAs(blob, `Faktura - ${invoice.bookedInvoiceNumber}.pdf`);
}